import React, { useEffect, useRef } from 'react';
import { TextField, InputAdornment } from '@mui/material';

import {
  formatCash,
  readCashInput,
  caretPositionForDigits,
} from '../../utils/formatCash';
import { centeredLabelSx } from '../../theme/fieldTokens';

// Grouped kroner field; state holds bare digits, see utils/formatCash.js
function CashField({
  label,
  value,
  onChange,
  autoFocus = false,
  width = '340px',
  fontSize = '26px',
  helperText,
  disabled = false,
  // Off by default: a counted drawer or a deposit has no negative side
  allowNegative = false,
}) {
  const inputRef = useRef(null);
  const caretDigitsRef = useRef(null);

  const handleChange = (event) => {
    const { value: digits, caretDigits } = readCashInput(event.target, {
      allowNegative,
    });
    caretDigitsRef.current = caretDigits;
    onChange(digits);
  };

  useEffect(() => {
    const el = inputRef.current;
    const target = caretDigitsRef.current;
    if (!el || target === null) return;

    caretDigitsRef.current = null;
    const pos = caretPositionForDigits(el.value, target);
    el.setSelectionRange(pos, pos);
  }, [value]);

  // The bare autoFocus prop loses the race against the modal mounting around it
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const id = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(id);
  }, [autoFocus, disabled]);

  return (
    <TextField
      autoComplete="off"
      label={label}
      value={formatCash(value)}
      variant="outlined"
      inputRef={inputRef}
      disabled={disabled}
      helperText={helperText}
      // Unshrunk the label is the placeholder, so it sits over the centred digits
      InputLabelProps={{ sx: centeredLabelSx }}
      inputProps={{
        inputMode: 'numeric',
        // Grouped text no longer matches [0-9]*, and a failing pattern marks the field invalid
        style: { textAlign: 'center', fontSize },
      }}
      InputProps={{
        // Mirrors the "kr" width so the digits centre on the field
        startAdornment: (
          <InputAdornment position="start">
            <span
              aria-hidden="true"
              style={{ visibility: 'hidden', fontSize: '20px' }}
            >
              kr
            </span>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <span style={{ color: '#8fa3d4', fontSize: '20px' }}>kr</span>
          </InputAdornment>
        ),
      }}
      onChange={handleChange}
      className="modal-textfield"
      style={{ width }}
    />
  );
}

export default CashField;
